/**
 * model-download.js — устойчивая загрузка тяжёлой модели (эталон общего блока).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ПРИМИТИВ. Тяжёлую модель (30–250 МБ) на мобильной сети
 * невозможно скачать «одним куском» — соединение рвётся. Замер на проде
 * (30 дней, `analytics_events`): 173 сессии в 8 тулах умерли на загрузке
 * модели, причём обрывы приходятся на РАЗНЫЕ позиции (0 / 33 КБ / 27 МБ из
 * 214 МБ) — это не баг кода, а свойство канала. Значит загрузчик обязан
 * переживать обрыв, а не сообщать о нём.
 *
 * Докачка у нас уже была написана ПЯТЬ раз (clap-worker, mdx-stem-separator,
 * stem-separator, omr-symbol-detect, supertonic-engine) и каждый раз заново.
 * Движки, которым её не донесли (gigaam-asr-worker), исправно ЗАМЕЧАЛИ обрыв
 * — `truncated download: 0/42508` — и падали. Этот файл существует, чтобы
 * шестой копии не было: он собирает уроки всех пяти в одном месте.
 *
 * УРОКИ, КАЖДЫЙ ОПЛАЧЕН ОТДЕЛЬНЫМ БАГОМ (не упрощать, не «чистить»):
 *
 *  1. Чисто закрывшийся, но КОРОТКИЙ поток — тоже обрыв. `read()` отдаёт
 *     `done:true`, и наивный загрузчик возвращает огрызок как успех.
 *     Сверять с `total` обязательно (stem-separator).
 *  2. `200` в ответ на Range = сервер/SW проигнорировал смещение и прислал
 *     ПОЛНОЕ тело → аккумулятор сбросить, иначе к докачке приклеится
 *     дубль префикса (supertonic, stem-separator).
 *  3. На `206` `content-length` — это ОСТАТОК, а не размер файла. Общий
 *     размер берётся из `Content-Range: bytes a-b/TOTAL` (stem-separator).
 *  4. `content-length` нельзя верить при transport-compression: getReader()
 *     отдаёт РАСПАКОВАННЫЕ байты, а заголовок считает сжатые — прогресс
 *     станет врать, а проверка полноты — ложно ронять загрузку. При
 *     `content-encoding` идём без знания размера (supertonic).
 *  5. Счётчик неудач сбрасывается ЛЮБЫМ прогрессом: лимит означает «подряд
 *     без единого байта», а не «всего за загрузку». Иначе 214 МБ по слабому
 *     каналу убиваются на пятом успешном возобновлении (supertonic).
 *  6. Сторож молчания ОБЯЗАН абортить fetch, а не отменять reader:
 *     `reader.cancel()` заставляет `read()` вернуть `done:true`, и зависший
 *     поток превращается в «успешную» усечённую загрузку (gigaam).
 *  7. Кэшировать поток можно ТОЛЬКО при полной загрузке с нуля. После
 *     докачки цельного ответа не существует — тогда в кэш кладутся собранные
 *     байты (`_mcPutModelBytes`). Об этом уже был комментарий в clap-worker,
 *     но пути для него не было.
 *
 * ⛔ Блок ИНЛАЙНОВЫЙ, а не importScripts: загрузка скрипта по сети на старте
 * воркера не имеет таймаута и при зависании убивает воркер целиком
 * (кластер apollo_worker_init_timeout). По той же причине инлайнится
 * model-cache-put. Копии сверяет ModelDownloadParityTest.
 *
 * Зависимость: рядом должен быть блок model-cache-put (`_mcCacheStream`,
 * `_mcPutModelBytes`). Если его нет — кэширование просто выключается.
 */
'use strict';

/* --8<-- model-download:begin — общий блок, копии сверяет ModelDownloadParityTest */
/* Устойчивая загрузка большого файла: докачка по Range + сторож молчания.
 *
 *   _mdFetchBytes(url, {
 *       onProgress(received, total),  // total = 0, когда размер неизвестен
 *       signal,                       // внешняя отмена (AbortSignal)
 *       stallMs,                      // молчание, после которого рвём (по умолч. 45 с)
 *       maxStalls,                    // сколько раз подряд терпим (по умолч. 4)
 *       cache                         // false — не писать в кэш моделей
 *   }) -> Uint8Array
 *
 * Возвращает ПОЛНЫЕ байты либо бросает. Частичный результат наружу не
 * отдаётся никогда — усечённая модель ломается позже и на другом слое, где
 * причину уже не видно. */
/* Урок 8, самый дорогой: ОБРЕЗАННЫЙ ОТВЕТ НЕЛЬЗЯ ОСТАВЛЯТЬ В КЭШЕ.
   Поток отдаётся кэшу ДО чтения тела (ради памяти), а полноту мы узнаём
   ПОСЛЕ. Замерено 17.08: ответ на 21254 байта с заголовком
   `content-length: 42508` записывается в `onnx-models` без единой
   жалобы — статус 200, запись валидная, переживает перезагрузку. Дальше
   Service Worker отдаёт этот огрызок ВМЕСТО модели, и тул не чинится
   больше ничем: ни повтором, ни F5, ни новой вкладкой. Это и есть
   «вгружаю файл неоднократно, она не принимает его».
   Поэтому неполнота обязана вычищать запись — тогда следующая попытка
   (и уже отравленные кэши у живых пользователей) лечится сама.

   ⚠️⚠️Чистить надо ВО ВСЕХ кэшах, а не в «своём». Замерено 17.08:
   `mel.bin` не заканчивается на `.onnx`, поэтому правило `onnx-models`
   его не ловит — его обслуживает catch-all `/vendor/` → SWR в кэше
   `vendor-v4`. Удаление «из своего кэша» промахивалось мимо
   отравленной записи, и цикл не разрывался: 181 запрос за 75 с,
   смещение не двигалось. Перебор кэшей стоит доли миллисекунды и не
   зависит от того, какое правило sw.js сработало сегодня, — а значит
   переживёт и следующую правку маршрутов. */
function dropCached(u) {
    try {
        if (typeof caches === 'undefined' || !caches.keys) return Promise.resolve();
        return caches.keys().then(function (names) {
            return Promise.all(names.map(function (n) {
                return caches.open(n).then(function (c) { return c.delete(u); })
                    .catch(function () {});
            }));
        }).catch(function () {});
    } catch (e) { return Promise.resolve(); }
}

async function _mdFetchBytes(url, opts) {
    opts = opts || {};
    var onProgress = opts.onProgress;
    var signal = opts.signal || null;
    var STALL_MS = opts.stallMs || 45000;
    var MAX_STALLS = (opts.maxStalls == null) ? 4 : opts.maxStalls;
    var wantCache = opts.cache !== false;
    /* Свой fetch — для мест, где глобальный подменён нами же (перехват
       весов в whisper-воркере): без этого примитив звал бы обёртку и
       уходил в бесконечную рекурсию. */
    var doFetch = opts.fetchImpl || fetch;

    var chunks = [], received = 0, total = 0, measurable = false;
    /* Урок 10 (29.10.2026, заявка на возврат №56): размер известен — байты
       идут сразу в буфер этого размера. Прежде куски копились в массив и в
       конце склеивались во второй буфер того же размера: в миг «скачано» в
       памяти лежали ДВЕ копии модели, ровно там, где iPhone убивал вкладку
       /music-genre-detector (CLAP 74 МБ: половина запусков в Safari умирала
       между скачиванием и сборкой сессии). Размер соврал — прежний путь. */
    var dest = null;
    function take(v) {
        if (dest && received + v.length <= dest.length) {
            dest.set(v, received);
        } else {
            if (dest) { chunks = [dest.subarray(0, received)]; dest = null; }
            chunks.push(v);
        }
        received += v.length;
    }
    var fails = 0, streamCached = false, resumed = false;
    var MIN_PROGRESS_BYTES = 256 * 1024;   // что считается «попытка продвинулась»
    var progressMark = 0;

    function externallyAborted() {
        return !!(signal && signal.aborted);
    }
    function abortError() {
        return (typeof DOMException !== 'undefined')
            ? new DOMException('Aborted', 'AbortError')
            : new Error('Aborted');
    }
    function sleep(ms) {
        return new Promise(function (res) { setTimeout(res, ms); });
    }

    for (;;) {
        if (externallyAborted()) throw abortError();

        /* Своё окно отмены на попытку: сторож молчания рвёт ЕГО, не трогая
           внешний signal, — иначе первое же зависание отменяло бы загрузку
           целиком вместо возобновления. */
        var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
        var onOuterAbort = null;
        if (ctrl && signal) {
            onOuterAbort = function () { try { ctrl.abort(); } catch (e) {} };
            try { signal.addEventListener('abort', onOuterAbort); } catch (e) {}
        }
        var stalled = false;
        var watchdog = null;
        /* Никаких лишних отличий от обычного fetch: `credentials:'omit'`
           здесь стоял и делал запрос анонимным, а вместе с этим менял то,
           что видно в Resource Timing — по которому воркер отличает «пришло
           из кэша» от «скачано». Загрузчику это не нужно, а телеметрии
           вредит. */
        var init = {};
        if (ctrl) init.signal = ctrl.signal;
        if (received > 0) init.headers = { Range: 'bytes=' + received + '-' };

        try {
            var resp;
            try {
                resp = await doFetch(url, init);
            } catch (e) {
                if (externallyAborted()) throw abortError();
                if (++fails > MAX_STALLS) throw e;
                await sleep(400 * fails);
                continue;
            }

            /* Урок 2: Range проигнорирован — пришло полное тело. */
            if (received > 0 && resp.status === 200) {
                chunks = []; dest = null; received = 0; total = 0; measurable = false; resumed = false;
            }
            if (!resp.ok && resp.status !== 206) {
                throw new Error('fetch ' + url + ' -> HTTP ' + resp.status);
            }

            /* Уроки 3 и 4: размер знаем только без transport-compression. */
            if (!measurable) {
                var enc = (resp.headers.get('content-encoding') || '').trim();
                var cl = parseInt(resp.headers.get('content-length') || '0', 10);
                if (!enc) {
                    if (resp.status === 206) {
                        var cr = resp.headers.get('content-range') || '';
                        var m = cr.match(/\/(\d+)\s*$/);
                        if (m) { total = parseInt(m[1], 10); measurable = true; }
                        else if (cl) { total = received + cl; measurable = true; }
                    } else if (cl) {
                        total = cl; measurable = true;
                    }
                }
            }

            if (measurable && total > 0 && !dest && received === 0 && !chunks.length) {
                try { dest = new Uint8Array(total); } catch (e) { dest = null; }
            }

            /* Урок 7: поток кэшируем, только пока он ЦЕЛЫЙ и с нуля. */
            if (wantCache && !resumed && received === 0 && resp.status === 200 &&
                typeof _mcCacheStream === 'function') {
                try { streamCached = _mcCacheStream(url, resp) || streamCached; } catch (e) {}
            }

            if (!resp.body || !resp.body.getReader) {
                var ab = await resp.arrayBuffer();
                take(new Uint8Array(ab));
                if (onProgress) onProgress(received, total);
                break;
            }

            var reader = resp.body.getReader();
            var lastTick = Date.now();
            /* Урок 6: рвём FETCH, а не reader. */
            watchdog = setInterval(function () {
                if (Date.now() - lastTick > STALL_MS) {
                    stalled = true;
                    if (ctrl) { try { ctrl.abort(); } catch (e) {} }
                }
            }, Math.min(5000, Math.max(1000, Math.floor(STALL_MS / 4))));

            var interrupted = false;
            try {
                for (;;) {
                    var r = await reader.read();
                    if (r.done) break;
                    take(r.value);
                    lastTick = Date.now();
                    /* Урок 5: прогресс прощает прошлые срывы — но только
                       СОДЕРЖАТЕЛЬНЫЙ. Безусловный сброс превращает
                       патологического отправителя (отдаёт по килобайту и
                       рвёт) в вечный цикл: счётчик обнуляется каждой
                       попыткой, и сдаться нельзя никогда. Поймано чужим
                       тестом tests/js/omr-model-download.test.mjs, который
                       моделирует ровно этот случай. Порог — 256 КиБ: реальный
                       обрыв на мобильном канале приносит мегабайты, а
                       килобайтная струйка обязана считаться отказом. */
                    if (received - progressMark >= MIN_PROGRESS_BYTES) {
                        progressMark = received;
                        fails = 0;
                    }
                    if (onProgress) onProgress(received, total);
                }
            } catch (e) {
                if (externallyAborted()) throw abortError();
                interrupted = true;         // обрыв или сторож — обе причины лечатся докачкой
            }

            /* Урок 1: короткий, но «успешно» закрывшийся поток — тот же обрыв. */
            if (!interrupted && measurable && total > 0 && received < total) interrupted = true;
            /* Урок 9 (22.10.2026): обрыв ПОСЛЕ последнего байта — не обрыв.
               Конец потока не пришёл (сторож) или чтение бросило, когда файл
               уже целиком здесь; докачка просила «bytes=N-», сервер отвечал
               416, и 305 МБ GigaAM выбрасывались с «gave up at N/N» (поле:
               3 загрузки за 4 дня на /audio-to-video). */
            if (interrupted && measurable && total > 0 && received === total) interrupted = false;

            if (!interrupted) break;

            /* Ответ оказался неполным — вычистить возможный огрызок в кэше.
               Он мог попасть туда как нашим потоковым кэшированием выше, так
               и прийти ИЗ кэша: тогда без удаления мы будем вечно получать
               те же куцые байты и вечно просить у сети один и тот же хвост
               (замер: 181 запрос за 75 с, смещение не двигалось). */
            if (wantCache) await dropCached(url);   // ДОЖДАТЬСЯ: иначе повтор успеет забрать тот же огрызок
            streamCached = false;

            if (++fails > MAX_STALLS) {
                throw new Error(
                    (stalled ? 'download stalled: ' : 'truncated download: ') +
                    received + '/' + (total || '?') + ' bytes from ' + url
                );
            }
            resumed = true;
            await sleep(400 * fails);
        } finally {
            if (watchdog) clearInterval(watchdog);
            if (onOuterAbort) { try { signal.removeEventListener('abort', onOuterAbort); } catch (e) {} }
        }
    }

    var out;
    if (dest && received === dest.length) {
        out = dest;
    } else if (dest) {
        out = dest.slice(0, received);
    } else if (chunks.length === 1) {
        out = chunks[0];
    } else {
        out = new Uint8Array(received);
        var off = 0;
        for (var i = 0; i < chunks.length; i++) { out.set(chunks[i], off); off += chunks[i].length; }
    }

    /* Урок 7 (вторая половина): докачанный файл потоком уже не сохранить —
       кладём собранные байты, иначе следующий визит снова качает всё.
       ⚠️ Условие «поток не кэшировался» здесь НЕ работает: _mcCacheStream
       возвращает true ОПТИМИСТИЧНО, не дожидаясь put(), а при обрыве put
       проваливается — флаг остаётся поднятым над несуществующей записью.
       Поэтому решает факт докачки, а не флаг. Двойной записи не будет:
       _mcPutModelBytes сам выходит, если запись в кэше уже есть. */
    if (wantCache && resumed && typeof _mcPutModelBytes === 'function') {
        try { _mcPutModelBytes(url, out); } catch (e) {}
    }
    _mdFetchBytes.lastStreamCached = streamCached;
    _mdFetchBytes.lastResumed = resumed;
    return out;
}

/* Устойчивый ОТВЕТ (а не байты): для потребителей, которые сами читают поток
 * и по нему считают прогресс — например, transformers.js в whisper-воркере.
 *
 *   _mdFetchResilient(url, init, opts) -> Response
 *
 * Возвращает Response, чьё тело переживает обрыв: соединение переоткрывается
 * с Range и поток продолжается с того места, где прервался. Потребитель
 * видит ОДИН непрерывный поток и ничего не знает о докачке — поэтому его
 * индикатор загрузки не сбрасывается и не врёт.
 *
 * Почему поток, а не байты: модель whisper — 90–250 МБ. Собрать её в память
 * целиком и отдать библиотеке готовым буфером значит держать вторую копию
 * ровно тогда, когда рядом создаётся ORT-сессия (второй горб памяти). На
 * телефоне это разница между «работает» и «вкладку убило».
 *
 * ⚠️ Отданное назад не забрать. Если на Range сервер ответит 200 (полное
 * тело), первые `delivered` байт надо ВЫБРОСИТЬ — потребитель их уже
 * получил, и повторная выдача склеит файл с дублем префикса. */
async function _mdFetchResilient(url, init, opts) {
    opts = opts || {};
    var MAX_STALLS = (opts.maxStalls == null) ? 4 : opts.maxStalls;
    var STALL_MS = (opts.stallMs == null) ? 45000 : opts.stallMs;
    var baseInit = init || {};
    var doFetch = opts.fetchImpl || fetch;

    /* Первый запрос тоже может не состояться — рвущийся канал не делает
       различий между «первый» и «следующий». Байтовый вариант это переживал,
       а здесь исключение уходило наружу и хоронило загрузку целиком (замер
       стенда: 11/12, падал сценарий «заголовки есть, тело пустое»). */
    var first = null, opened = 0;
    for (;;) {
        try { first = await doFetch(url, baseInit); break; }
        catch (e) {
            if (++opened > MAX_STALLS) throw e;
            await new Promise(function (r) { setTimeout(r, 400 * opened); });
        }
    }
    /* Не наш случай — отдаём как есть: ошибка, редирект, тело без потока. */
    if (!first.ok || !first.body || !first.body.getReader) return first;

    var enc = (first.headers.get('content-encoding') || '').trim();
    var cl = parseInt(first.headers.get('content-length') || '0', 10);
    var total = (!enc && cl) ? cl : 0;

    var reader = first.body.getReader();
    var delivered = 0, fails = 0;

    function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
    /* cancel() у оборвавшегося потока — отклонённый промис, и try/catch его не
       ловит: каждая докачка оставляла «unhandledrejection» в журнале страницы. */
    function quietCancel(rd, reason) {
        try { var p = rd.cancel(reason); if (p && p.catch) p.catch(function () {}); } catch (e) {}
    }

    /* Переоткрыть с места обрыва. Возвращает reader либо null, если нечем. */
    async function resume(controller) {
        /* Тот же урок 8, что и у байтового пути: обрезанный ответ мог уже
           осесть в кэше — правило `/cdn/hf/` в sw.js кладёт туда ЛЮБОЙ ok,
           длину не сверяя. Не вычистив его, докачка будет вечно получать те
           же куцые байты из кэша вместо файла. */
        if (opts.cache !== false) { try { await dropCached(url); } catch (e) {} }
        for (;;) {
            if (++fails > MAX_STALLS) return null;
            await sleep(400 * fails);
            var resp;
            try {
                var ri = {};
                for (var k in baseInit) {
                    if (Object.prototype.hasOwnProperty.call(baseInit, k)) ri[k] = baseInit[k];
                }
                ri.headers = { Range: 'bytes=' + delivered + '-' };
                resp = await doFetch(url, ri);
            } catch (e) { continue; }
            if (!resp.ok && resp.status !== 206) continue;
            if (!resp.body || !resp.body.getReader) continue;

            var rd = resp.body.getReader();
            if (resp.status === 200 && delivered > 0) {
                /* Range проигнорирован — пришло полное тело. Проглатываем уже
                   отданный префикс, чтобы вниз не уехал дубль. */
                var skip = delivered, dead = false;
                while (skip > 0) {
                    var s = await rd.read();
                    if (s.done) { dead = true; break; }
                    if (s.value.length <= skip) { skip -= s.value.length; continue; }
                    controller.enqueue(s.value.subarray(skip));
                    delivered += s.value.length - skip;
                    skip = 0;
                }
                if (dead) continue;
            }
            return rd;
        }
    }

    var stream = new ReadableStream({
        async pull(controller) {
            for (;;) {
                var chunk = null, broke = false;
                try {
                    var timer = null;
                    var race = [reader.read()];
                    if (STALL_MS > 0) {
                        race.push(new Promise(function (_, rej) {
                            timer = setTimeout(function () { rej(new Error('stall')); }, STALL_MS);
                        }));
                    }
                    var r = await Promise.race(race);
                    if (timer) clearTimeout(timer);
                    if (r.done) {
                        if (total && delivered < total) broke = true;   // короткий поток — тоже обрыв
                        else { controller.close(); return; }
                    } else {
                        chunk = r.value;
                    }
                } catch (e) {
                    if (timer) clearTimeout(timer);
                    broke = true;
                }

                if (chunk) {
                    delivered += chunk.length;
                    fails = 0;                     // прогресс прощает прошлые срывы
                    controller.enqueue(chunk);
                    return;
                }

                if (broke && total && delivered === total) {         // урок 9: всё уже отдано
                    quietCancel(reader);
                    controller.close();
                    return;
                }
                if (broke) {
                    quietCancel(reader);
                    var next = await resume(controller);
                    if (!next) {
                        controller.error(new Error('resumable fetch gave up at '
                            + delivered + '/' + (total || '?') + ' for ' + url));
                        return;
                    }
                    reader = next;
                }
            }
        },
        cancel: function (reason) {
            quietCancel(reader, reason);
        }
    });

    return new Response(stream, {
        status: first.status,
        statusText: first.statusText,
        headers: first.headers
    });
}
/* --8<-- model-download:end */

if (typeof self !== 'undefined' && typeof self.__mdFetchBytes === 'undefined') {
    self.__mdFetchBytes = _mdFetchBytes;
}
if (typeof self !== 'undefined' && typeof self.__mdFetchResilient === 'undefined') {
    self.__mdFetchResilient = _mdFetchResilient;
}
